import { useEffect, useRef, useState } from 'react'
import CanvasJSReact from '@canvasjs/react-charts'

import {
  CHART_DEFAULT_OPTIONS,
  LABEL_FONT_SIZE,
  SUBTITLE_FONT_SIZE,
  TITLE_FONT_SIZE,
  UnitConversions,
  UserUnitSettings,
} from '../../common/constants'
import { formatDateForChart, roundToDecimal } from '../../common/utils'
import { ChartSubHeader } from '../ChartSubHeader'
import { CollapsibleSection } from '../CollapsibleSection'
import { convertBaseToDisplay } from '../../common/conversions'
import { DeviceInfo, SensorProduct, UnitCategoryId } from '../../common/enums'
import { Viewport } from '../../common/types'

const { CanvasJSChart } = CanvasJSReact

const CHART_TYPE_LINE = 'line'

const CHART_NAME: Record<any, string> = {
  [SensorProduct.AquaTroll500DissolvedOxygenSensor]: 'Total Dissolved Oxygen',
  [SensorProduct.AquaTroll500NitrateSensor]: 'Nitrate',
  [SensorProduct.AquaTroll500SalinitySensor]: 'Salinity',
  [SensorProduct.AquaTroll500SpecificConductivitySensor]: 'Specific Conductivity',
  [SensorProduct.AquaTroll500TotalDissolvedSolidsSensor]: 'Total Dissolved Solids',
  [SensorProduct.AquaTroll500TurbiditySensor]: 'Turbidity',
  [SensorProduct.AquaTroll500TemperatureSensor]: 'Water Temperature',
  [SensorProduct.Battery]: 'Battery',
  [SensorProduct.Humidity]: 'Humidity',
  [SensorProduct.IrrometerWM200SS]: 'Soil Moisture',
  [SensorProduct.Pressure]: 'Pressure',
  [SensorProduct.SolarRadiation]: 'Solar Radiation',
  [SensorProduct.Temperature]: 'Temperature',
  [SensorProduct.WindSpeed]: 'Wind Speed',
  [SensorProduct.OpticalRainSensorSAB]: 'Rainfall (ORS SAB)',
  [SensorProduct.OpticalRainSensorPower]: 'Rainfall (ORS Power)',
}

interface Props {
  device: DeviceInfo
  isLoading: boolean
  sensorProduct: SensorProduct
  sensorPort: string
  sensorReadings: any
  viewport: Viewport
  userUnitSettings: UserUnitSettings
  unitConversions: UnitConversions

  zoomEnabled?: boolean
  selectXRange?: number[]
  addTimestamp?: (timestamp: number) => void
}

export function GenericLineChart({
  device,
  isLoading,
  sensorProduct,
  sensorPort,
  sensorReadings,
  viewport,
  userUnitSettings,
  unitConversions,

  zoomEnabled = true,
  selectXRange = [],
  addTimestamp = () => {},
}: Props) {
  const ref = useRef(null)
  const chartName = CHART_NAME[sensorProduct] || 'Unknown Sensor Type'

  const [x, setX] = useState(0)
  const [y, setY] = useState(0)

  let trackDay = -1

  useEffect(() => {
    if (ref === null) return

    const canvas = document.getElementsByClassName('canvasjs-chart-canvas')[1]
    canvas.addEventListener('click', onClickTest)

    function onClickTest() {
      addTimestamp(x)
    }

    return () => {
      canvas.removeEventListener('click', onClickTest)
    }
  }, [ref, x, y]) // eslint-disable-line react-hooks/exhaustive-deps

  const timestamps = sensorReadings ? Object.keys(sensorReadings.readings) : []

  const filterMinimumValue: number | undefined = undefined
  const filterMaximumValue: number | undefined = undefined

  const unitCategoryId =
    sensorProduct === SensorProduct.AquaTroll500TemperatureSensor
      ? UnitCategoryId.WaterTemperature
      : sensorReadings.metadata.units.category.id
  const unitId = userUnitSettings[unitCategoryId].unitId
  const units = unitConversions[unitCategoryId as UnitCategoryId].abbreviations[unitId]
  const formula = unitConversions[unitCategoryId as UnitCategoryId].formulas[unitId]
  const precision = userUnitSettings[unitCategoryId].precision
  const reverseYAxis = sensorReadings.metadata.productName.toLowerCase().includes('irrometer')

  const minTimestamp = timestamps.length > 0 ? parseInt(timestamps[0]) * 1000 : viewport.minimum
  const maxTimestamp = timestamps.length > 0 ? parseInt(timestamps[timestamps.length - 1]) * 1000 : viewport.maximum
  const minX = viewport.minimum > minTimestamp ? viewport.minimum : minTimestamp
  const maxX = viewport.maximum < maxTimestamp ? viewport.maximum : maxTimestamp

  const readings = sensorReadings
    ? sensorPort === 'T0' && sensorProduct === SensorProduct.AquaTroll500TemperatureSensor
      ? convertTemperatureForAquaTroll(sensorReadings.readings)
      : sensorReadings.readings
    : []

  const dataPoints = timestamps
    .map((key: string) => {
      const ts = parseInt(key)

      const reading = convertBaseToDisplay(Number(readings[ts]), formula)

      return {
        x: ts * 1000,
        y: roundToDecimal(reading, precision),
      }
    })
    .filter((dataPoint: any) => (filterMinimumValue !== undefined ? dataPoint.y > filterMinimumValue : true))
    .filter((dataPoint: any) => (filterMaximumValue !== undefined ? dataPoint.y < filterMaximumValue : true))

  const isThereAnyDataToRender =
    dataPoints.filter((dataPoint: any) => dataPoint.x > minX).filter((dataPoint: any) => dataPoint.x < maxX).length > 0

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_${chartName}`,
    title: {
      text: chartName,
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`

        for (var i = 0; i < e.entries.length; i++) {
          content += `<span style="color:${e.entries[i].dataSeries.color}">${
            e.entries[i].dataSeries.name
          }:</span> <strong> ${roundToDecimal(e.entries[i].dataPoint.y, precision)} ${units} </strong><br/>`
        }

        setX(e.entries[0].dataPoint.x)
        setY(e.entries[0].dataPoint.y)
        return content
      },
    },
    axisY: {
      includeZero: false,
      title: `${chartName} (${units})`,
      titleFontSize: TITLE_FONT_SIZE,
      labelFontSize: LABEL_FONT_SIZE,
      reversed: reverseYAxis,
    },
    axisX: {
      labelFormatter: function (e: any) {
        const day = new Date(e.value).getDay()
        const dayChanged = trackDay !== day
        trackDay = day

        return formatDateForChart(new Date(e.value), dayChanged)
      },
      viewportMinimum: minX || null,
      viewportMaximum: maxX || null,
      stripLines: [
        ...selectXRange.map((value) => {
          return {
            value,
            color: 'red',
            labelPlacement: 'outside',
          }
        }),

        selectXRange.length === 2 && {
          startValue: selectXRange[0],
          endValue: selectXRange[1],
          color: '#d8d8d8',
        },
      ],
    },
    data: [
      {
        name: chartName,
        type: CHART_TYPE_LINE,
        showInLegend: true,
        xValueType: 'dateTime',
        dataPoints,
      },
    ],
  }

  function getLastReadingTimestamp(): string {
    return sensorReadings && sensorReadings.metadata.timestamps.end !== 0
      ? new Date(sensorReadings.metadata.timestamps.end * 1000).toLocaleString()
      : 'No readings'
  }

  const data: string[][] = [
    [
      `Port ${sensorPort === 'T0' ? 'C3' : sensorPort}: ${sensorReadings.metadata.productName}`,
      `${getLastReadingTimestamp()}`,
      `${roundToDecimal(dataPoints[dataPoints.length - 1]?.y || 0, precision)} ${units}`,
    ],
  ]

  if (sensorProduct === SensorProduct.IrrometerWM200SS) {
    const depth = sensorReadings.metadata.sensorConfig.offset
    data[0].splice(2, 0, `Depth: ${depth}"`)
  }

  return (
    <CollapsibleSection title={chartName} open={true} isLoading={isLoading}>
      <ChartSubHeader data={data} />
      {isThereAnyDataToRender ? (
        <CanvasJSChart ref={ref} options={options} />
      ) : (
        <div className={tailwind.noReadingsMessage}>This RIC has no data for the selected period</div>
      )}
    </CollapsibleSection>
  )
}

const tailwind = {
  noReadingsMessage:
    'bg-red-500 text-white font-bold rounded px-4 py-2 mb-10 mt-6 h-10 w-[320px] h-[70px] mx-auto text-center',
}

// hack to fix temperature bug for AquaTroll500TemperatureSensor (C3 scaling parameters not sent from firmware, which is used to convert the temperature properly)
function convertTemperatureForAquaTroll(data: any) {
  const result = { ...data }

  for (const key in result) {
    const transmittedValue = (result[key] + 40) * 32 // reverse old temperature conversion
    const newTemperature = transmittedValue / 50 - 5 // use AquaTroll500TemperatureSensor as per specification (even though the C3 scaling parameters are not sent from the firmware)
    result[key] = newTemperature
  }
  return result
}
